/**
 * Built-in Transform Plugins
 * 
 * These transforms are registered by default and demonstrate
 * how to create custom transformation plugins.
 */

import { pluginRegistry } from './PluginRegistry.js';
import { 
  mirrorCoordinates, 
  getWaypointIndicesToMirror 
} from '../services/MirrorService.js';
import { 
  applyOffset, 
  scalePosition 
} from '../services/WaypointService.js';

/**
 * Offset Transform - Move waypoints by a fixed offset
 */
export const offsetTransform = {
  id: 'offset',
  name: 'Offset',
  description: 'Move waypoints by a fixed offset in X, Y, Z',
  category: 'translate',
  
  transform: (waypoints, options) => {
    const { offsetX = 0, offsetY = 0, offsetZ = 0, indices = null } = options;
    const offset = { x: offsetX, y: offsetY, z: offsetZ };
    
    return waypoints.map((wp, i) => {
      if (indices && !indices.includes(i)) {
        return wp;
      }
      
      return {
        ...wp,
        position: applyOffset(wp.position, offset),
        viaPoint: wp.viaPoint ? {
          ...wp.viaPoint,
          position: applyOffset(wp.viaPoint.position, offset)
        } : null
      };
    });
  },
  
  validate: (options) => {
    const errors = [];
    if (typeof options.offsetX !== 'undefined' && typeof options.offsetX !== 'number') {
      errors.push('offsetX must be a number');
    }
    if (typeof options.offsetY !== 'undefined' && typeof options.offsetY !== 'number') {
      errors.push('offsetY must be a number');
    }
    if (typeof options.offsetZ !== 'undefined' && typeof options.offsetZ !== 'number') {
      errors.push('offsetZ must be a number');
    }
    return { valid: errors.length === 0, errors };
  }
};

/**
 * Scale Transform - Scale waypoint positions
 */
export const scaleTransform = {
  id: 'scale',
  name: 'Scale',
  description: 'Scale waypoint positions by a factor',
  category: 'scale',
  
  transform: (waypoints, options) => {
    const { scale = 1, scaleX, scaleY, scaleZ, origin = { x: 0, y: 0, z: 0 }, indices = null } = options;
    
    // Use individual axis scales or uniform scale
    const sx = scaleX ?? scale;
    const sy = scaleY ?? scale;
    const sz = scaleZ ?? scale;
    
    return waypoints.map((wp, i) => {
      if (indices && !indices.includes(i)) {
        return wp;
      }
      
      // Scale relative to origin
      const scaledPosition = {
        x: origin.x + (wp.position.x - origin.x) * sx,
        y: origin.y + (wp.position.y - origin.y) * sy,
        z: origin.z + (wp.position.z - origin.z) * sz
      };
      
      let scaledViaPoint = null;
      if (wp.viaPoint) {
        scaledViaPoint = {
          ...wp.viaPoint,
          position: {
            x: origin.x + (wp.viaPoint.position.x - origin.x) * sx,
            y: origin.y + (wp.viaPoint.position.y - origin.y) * sy,
            z: origin.z + (wp.viaPoint.position.z - origin.z) * sz
          }
        };
      }
      
      return {
        ...wp,
        position: scaledPosition,
        viaPoint: scaledViaPoint
      };
    });
  },
  
  validate: (options) => {
    const errors = [];
    const { scale, scaleX, scaleY, scaleZ } = options;
    
    if (scale !== undefined && (typeof scale !== 'number' || scale <= 0)) {
      errors.push('scale must be a positive number');
    }
    if (scaleX !== undefined && (typeof scaleX !== 'number' || scaleX <= 0)) {
      errors.push('scaleX must be a positive number');
    }
    if (scaleY !== undefined && (typeof scaleY !== 'number' || scaleY <= 0)) {
      errors.push('scaleY must be a positive number');
    }
    if (scaleZ !== undefined && (typeof scaleZ !== 'number' || scaleZ <= 0)) {
      errors.push('scaleZ must be a positive number');
    }
    
    return { valid: errors.length === 0, errors };
  }
};

/**
 * Rotate Transform - Rotate waypoints around an axis
 */
export const rotateTransform = {
  id: 'rotate',
  name: 'Rotate',
  description: 'Rotate waypoints around an axis through a point',
  category: 'rotate',
  
  transform: (waypoints, options) => {
    const { 
      angle = 0, // in radians
      axis = 'z', // 'x', 'y', or 'z'
      origin = { x: 0, y: 0, z: 0 },
      indices = null 
    } = options;
    
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    
    const rotatePoint = (point) => {
      // Translate to origin
      const px = point.x - origin.x;
      const py = point.y - origin.y;
      const pz = point.z - origin.z;
      
      let rx, ry, rz;
      
      switch (axis) {
        case 'x':
          rx = px;
          ry = py * cos - pz * sin;
          rz = py * sin + pz * cos;
          break;
        case 'y':
          rx = px * cos + pz * sin;
          ry = py;
          rz = -px * sin + pz * cos;
          break;
        case 'z':
        default:
          rx = px * cos - py * sin;
          ry = px * sin + py * cos;
          rz = pz;
          break;
      }
      
      // Translate back
      return {
        x: rx + origin.x,
        y: ry + origin.y,
        z: rz + origin.z
      };
    };
    
    return waypoints.map((wp, i) => {
      if (indices && !indices.includes(i)) {
        return wp;
      }
      
      return {
        ...wp,
        position: rotatePoint(wp.position),
        viaPoint: wp.viaPoint ? {
          ...wp.viaPoint,
          position: rotatePoint(wp.viaPoint.position)
        } : null
      };
    });
  },
  
  validate: (options) => {
    const errors = [];
    
    if (typeof options.angle !== 'number') {
      errors.push('angle must be a number (in radians)');
    }
    if (options.axis && !['x', 'y', 'z'].includes(options.axis)) {
      errors.push('axis must be "x", "y", or "z"');
    }
    
    return { valid: errors.length === 0, errors };
  }
};

/**
 * Reverse Transform - Reverse the order of waypoints
 */
export const reverseTransform = {
  id: 'reverse',
  name: 'Reverse Order',
  description: 'Reverse the order of waypoints (useful for bidirectional paths)',
  category: 'reorder',
  
  transform: (waypoints, options) => {
    const { indices = null, renumber = true } = options;
    
    if (indices) {
      // Reverse only specific indices
      const result = [...waypoints];
      const selectedWaypoints = indices.map(i => waypoints[i]);
      selectedWaypoints.reverse();
      indices.forEach((originalIndex, i) => {
        result[originalIndex] = selectedWaypoints[i];
      });
      return result;
    }
    
    // Reverse all and optionally renumber
    const reversed = [...waypoints].reverse();
    
    if (renumber) {
      return reversed.map((wp, i) => ({
        ...wp,
        number: i + 1
      }));
    }
    
    return reversed;
  },
  
  validate: () => ({ valid: true, errors: [] })
};

/**
 * Register all built-in transforms
 */
export function registerBuiltInTransforms() {
  pluginRegistry.registerTransform(offsetTransform);
  pluginRegistry.registerTransform(scaleTransform);
  pluginRegistry.registerTransform(rotateTransform);
  pluginRegistry.registerTransform(reverseTransform);
}
